import { useState, useEffect } from 'react';
import { collection, query, where, orderBy, onSnapshot, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebaseClient';

export const useNotifications = (userId, userRole) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!userId) return;

    // Query for notifications where user is mentioned or involved
    const q = query(
      collection(db, 'comments'),
      where('item_type', 'in', ['assignment', 'class']),
      orderBy('created_at', 'desc')
    );

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const allComments = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        created_at: doc.data().created_at?.toDate() || new Date()
      }));

      let relevantNotifications = [];

      if (userRole === 'admin' || userRole === 'writer' || userRole === 'qa') {
        // For admin/writer/QA users: show all client comments and replies to their comments
        relevantNotifications = allComments.filter(comment => {
          // Show client comments on any assignment/class
          if (comment.author_role === 'client' && comment.author_id !== userId) {
            return true;
          }
          
          // Show replies to admin's comments
          if (comment.parent_id && comment.author_id !== userId) {
            const parentComment = allComments.find(c => c.id === comment.parent_id);
            return parentComment && parentComment.author_id === userId;
          }
          
          return false;
        });
      } else {
        // For client users: show admin/writer comments on their items
        const userAssignments = await getUserAssignments(userId);
        const userClasses = await getUserClasses(userId);
        
        const userItemIds = [
          ...userAssignments.map(a => a.id),
          ...userClasses.map(c => c.id)
        ];

        relevantNotifications = allComments.filter(comment => {
          // Show admin/writer comments on user's items
          if (userItemIds.includes(comment.item_id) && 
              comment.author_id !== userId && 
              (comment.author_role === 'admin' || comment.author_role === 'writer')) {
            return true;
          }
          
          // Show replies to user's comments
          if (comment.parent_id && comment.author_id !== userId) {
            const parentComment = allComments.find(c => c.id === comment.parent_id);
            return parentComment && parentComment.author_id === userId;
          }
          
          return false;
        });
      }

      // Transform comments into proper activity feed format
      relevantNotifications = relevantNotifications.map(comment => ({
        ...comment,
        title: comment.parent_id 
          ? `Reply from ${comment.author_name}` 
          : `New ${comment.item_type} comment from ${comment.author_name}`,
        body: comment.content,
        type: comment.item_type || 'comment',
        metadata: {
          author_name: comment.author_name,
          author_role: comment.author_role,
          item_title: comment.item_title,
          item_type: comment.item_type,
          item_id: comment.item_id
        }
      }));

      setNotifications(relevantNotifications);
      setUnreadCount(relevantNotifications.filter(n => !n.read_by?.includes(userId)).length);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [userId, userRole]);

  const markAsRead = async (notificationId) => {
    try {
      const notificationRef = doc(db, 'comments', notificationId);
      await updateDoc(notificationRef, {
        read_by: [...(notifications.find(n => n.id === notificationId)?.read_by || []), userId]
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const unreadNotifications = notifications.filter(n => !n.read_by?.includes(userId));
      await Promise.all(
        unreadNotifications.map(notification => markAsRead(notification.id))
      );
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      const notificationRef = doc(db, 'comments', notificationId);
      await deleteDoc(notificationRef);
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  return {
    notifications,
    loading,
    unreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification
  };
};

// Helper functions to get user's assignments and classes
const getUserAssignments = async (userId) => {
  const q = query(
    collection(db, 'assignments'),
    where('user_id', '==', userId)
  );
  
  return new Promise((resolve) => {
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const assignments = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      resolve(assignments);
      unsubscribe();
    });
  });
};

const getUserClasses = async (userId) => {
  const q = query(
    collection(db, 'class_management'),
    where('user_id', '==', userId)
  );
  
  return new Promise((resolve) => {
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const classes = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      resolve(classes);
      unsubscribe();
    });
  });
};